<!-- Country-aware form for editing a patient's personal and address details, with PESEL support. -->

<script lang="ts" setup>
import type { PartialNullable } from '@nzyme/types/Object.js';
import { assert } from '@nzyme/utils/assert.js';
import { Form } from '@nzyme/vue-forms/components/Form.jsx';
import { useForm } from '@nzyme/vue-forms/useForm.js';
import { useFormFields } from '@nzyme/vue-forms/useFormFields.js';
import { postCodeValidator } from '@nzyme/vue-forms/validators/postCodeValidator.js';
import { requiredValidator } from '@nzyme/vue-forms/validators/requiredValidator.js';
import { useTranslate } from '@nzyme/vue-i18n/useTranslate.js';
import { useLogger } from '@nzyme/vue-ioc/useLogger.js';
import { useService } from '@nzyme/vue-ioc/useService.js';
import { useEmitAsync } from '@nzyme/vue-utils/useEmitAsync.js';
import { Reveal } from '@nzyme/vue/components/Reveal.jsx';
import { computed, ref, watch } from 'vue';

import { peselValidator } from '@sigma/client/forms/peselValidation.js';
import { CountryContext } from '@sigma/common/i18n/CountryContext.js';
import { parsePesel } from '@sigma/common/i18n/parsePesel.js';
import { shouldSkipPeselValidation } from '@sigma/common/i18n/shouldSkipPeselValidation.js';
import type { PatientProfile } from '@sigma/common/patient/PatientProfile.js';
import { formatDateIso } from '@sigma/utils/format/formatDateIso.js';

import Button from '../Button.vue';
import Checkbox from '../forms/Checkbox.vue';
import CountryInput from '../forms/CountryInput.vue';
import DateInput from '../forms/DateInput.vue';
import GenderInput from '../forms/GenderInput.vue';
import PeselInput from '../forms/PeselInput.vue';
import PostCodeInput from '../forms/PostCodeInput.vue';
import StateInput from '../forms/StateInput.vue';
import TextInput from '../forms/TextInput.vue';
import Layout from '../layout/Layout';
import * as lp from './PatientProfile.loc.js';
import * as l from './PatientProfileForm.loc.js';

export type { PatientProfile };

interface Props {
  profile: PartialNullable<PatientProfile>;
  submit: (profile: PatientProfile) => Promise<void>;
  title?: string;
  subtitle?: string;
}

interface Emits {
  saved: [];
}

const props = defineProps<Props>();
defineEmits<Emits>();

const emit = useEmitAsync<Emits>();
const t = useTranslate();
const logger = useLogger('PatientProfileForm');
const countryContext = useService(CountryContext);

const country = computed(() => countryContext());
const skipPesel = computed(() => shouldSkipPeselValidation(country.value));
const isUS = computed(() => country.value === 'US');

// Loading state - for non-PL countries always show birth date/gender fields
const noPesel = ref(
  skipPesel.value || (!props.profile.data?.PL_PESEL && (!!props.profile.birthDate || !!props.profile.gender)),
);

// Form state
const form = useForm({
  firstName: props.profile.firstName?.trim() || '',
  lastName: props.profile.lastName?.trim() || '',
  birthDate: props.profile.birthDate ? new Date(props.profile.birthDate) : null,
  gender: props.profile.gender || null,
  address: {
    country: isUS.value ? 'US' : props.profile.address?.country || country.value,
    city: props.profile.address?.city?.trim() || '',
    street: props.profile.address?.street?.trim() || '',
    postCode: props.profile.address?.postCode?.trim() || '',
    state: props.profile.address?.state?.trim() || null,
  },
  data: {
    PL_PESEL: skipPesel.value ? '' : props.profile.data?.PL_PESEL?.trim() || '',
    DOCUMENT_NUMBER: props.profile.data?.DOCUMENT_NUMBER?.trim() || '',
  },
});

const fields = useFormFields(form, {
  firstName: [requiredValidator()],
  lastName: [requiredValidator()],
  birthDate: [requiredValidator()],
  gender: [requiredValidator()],
  address: field =>
    useFormFields(field, {
      country: [requiredValidator()],
      city: [requiredValidator()],
      street: [requiredValidator()],
      postCode: [
        requiredValidator(),
        postCodeValidator({
          country: () => form.value.address.country,
        }),
      ],
      state: [
        requiredValidator({
          condition: () => isUS.value,
        }),
      ],
    }),
  data: field =>
    useFormFields(field, {
      PL_PESEL: [
        requiredValidator({
          condition: () => !noPesel.value,
        }),
        peselValidator(),
      ],
      DOCUMENT_NUMBER: [
        requiredValidator({
          condition: () =>
            noPesel.value &&
            // Document number is PL-specific
            !skipPesel.value,
        }),
      ],
    }),
});

const pesel = computed(() => parsePesel(form.value.data.PL_PESEL));

// Watch PESEL changes and auto-update birth date and gender
watch(pesel, newPesel => {
  if (!newPesel) {
    return;
  }

  form.value.birthDate = newPesel.birthDate;
  form.value.gender = newPesel.gender;
});

watch(noPesel, noPeselEnabled => {
  if (noPeselEnabled) {
    form.value.data.PL_PESEL = '';
  } else {
    form.value.data.PL_PESEL = props.profile.data?.PL_PESEL || '';
  }
});

async function onSubmit() {
  const valid = await form.validate();
  if (!valid) {
    return;
  }

  assert(form.value.birthDate, 'Birth date is required');
  assert(form.value.gender, 'Gender is required');

  await props.submit({
    birthDate: formatDateIso(form.value.birthDate),
    firstName: form.value.firstName.trim(),
    gender: form.value.gender,
    lastName: form.value.lastName.trim(),
    address: {
      country: form.value.address.country,
      city: form.value.address.city.trim(),
      street: form.value.address.street.trim(),
      postCode: form.value.address.postCode.trim(),
      state: form.value.address.state?.trim() || null,
    },
    data: {
      PL_PESEL: form.value.data.PL_PESEL.trim() || null,
      DOCUMENT_NUMBER: form.value.data.DOCUMENT_NUMBER.trim() || null,
    },
  });

  logger.debug('Profile updated successfully');
  await emit('saved');
}
</script>

<template>
  <Form
    v-if="profile"
    :class="css.form"
    @submit="onSubmit"
  >
    <Layout>
      <template #title>
        {{ title || t(l.title) }}
      </template>

      <template #subtitle>
        {{ subtitle || t(l.subtitle) }}
      </template>

      <slot />

      <div :class="css.row">
        <TextInput
          :field="fields.firstName"
          :label="t(lp.firstName)"
          autocomplete="given-name"
        />

        <TextInput
          :field="fields.lastName"
          :label="t(lp.lastName)"
          autocomplete="family-name"
        />
      </div>

      <PeselInput
        v-if="!skipPesel"
        :class="css.field"
        :field="fields.data.PL_PESEL"
        :label="t(lp.pesel)"
        :disabled="noPesel"
      />

      <section>
        <Checkbox
          v-if="!skipPesel"
          v-model="noPesel"
          :class="css.field"
        >
          {{ t(l.noPeselCheckbox) }}
        </Checkbox>

        <Reveal>
          <div v-if="noPesel">
            <div :class="css.row">
              <DateInput
                :class="css.field"
                :field="fields.birthDate"
                :max="new Date()"
                :label="t(lp.birthDate)"
                autocomplete="bday"
              />

              <GenderInput
                :label="t(lp.gender)"
                :class="css.field"
                :field="fields.gender"
              />
            </div>

            <TextInput
              v-if="noPesel && !skipPesel"
              :class="css.field"
              :field="fields.data.DOCUMENT_NUMBER"
              :label="t(lp.documentNumber)"
            />
          </div>
        </Reveal>
      </section>

      <section>
        <h3 :class="css.sectionTitle">
          {{ t(lp.address) }}
        </h3>

        <!-- US: Street → City, State → ZIP -->
        <template v-if="isUS">
          <TextInput
            :class="css.field"
            :field="fields.address.street"
            :label="t(lp.addressStreet)"
            autocomplete="street-address"
          />

          <div :class="css.row">
            <TextInput
              :field="fields.address.city"
              :label="t(lp.addressCity)"
              autocomplete="address-level2"
            />

            <StateInput
              :label="t(lp.addressState)"
              :field="fields.address.state"
              :country="form.value.address.country"
            />
          </div>

          <PostCodeInput
            :class="css.field"
            :field="fields.address.postCode"
            :country="form.value.address.country"
          />
        </template>

        <!-- PL/European: Street → Post Code, City → Country -->
        <template v-else>
          <TextInput
            :class="css.field"
            :field="fields.address.street"
            :label="t(lp.addressStreet)"
            autocomplete="street-address"
          />

          <div :class="css.row">
            <PostCodeInput
              :label="t(lp.addressPostCode)"
              :field="fields.address.postCode"
              :country="form.value.address.country"
            />

            <TextInput
              :field="fields.address.city"
              :label="t(lp.addressCity)"
              autocomplete="address-level2"
            />
          </div>

          <CountryInput
            :class="css.field"
            :field="fields.address.country"
          />
        </template>
      </section>

      <template #footer>
        <Button
          :class="css.submit"
          type="submit"
          variant="primary"
          color="black"
          rounded
          size="large"
        >
          {{ t(l.saveButton) }}
        </Button>
      </template>
    </Layout>
  </Form>
</template>

<style lang="scss" module="css">
.form {
  display: flex;
  flex-direction: column;
}

.message {
  margin-bottom: $spacing-16;
}

.row {
  display: grid;
  grid-template-columns: 1fr 1fr;
  column-gap: $spacing-16;
  row-gap: $spacing-12;
  margin-top: $spacing-12;

  @include media-max-sm {
    grid-template-columns: 1fr;
  }
}

.field {
  margin-top: $spacing-12;
}

.sectionTitle {
  font-size: $font-size-18;
  font-weight: 600;
  margin-top: $spacing-20;
}
</style>
